import { Location } from '@angular/common';
import { DestroyRef, inject, Injectable, signal, TemplateRef } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, map, mergeMap } from 'rxjs';
import { HeaderConfig, TitleSource } from 'src/app/core/models/header-config';
import { StepperNavigationService } from 'src/app/features/finance/pages/debit-order-stepper/services/stepper-navigation.service';
import { TeacherNamePipe } from 'src/app/shared/pipes/teacher-name.pipe';
import { selectSelectedClass } from '../../store/academics/academic.selectors';
import { selectSelectedActivity } from '../../store/activities/activity.selectors';
import { AppState } from '../../store/app.state';
import { selectSelectedEvent } from '../../store/events/event.selectors';
import { selectSelectedAccountLinks } from '../../store/finance/finance.selectors';
import { selectSelectedSchool } from '../../store/schools/school.selectors';
import { selectSelectedStudent } from '../../store/students/student.selectors';
import { RouterService } from '../router/router.service';
import { SupportService } from '../support/support.service';

@Injectable({ providedIn: 'root' })
export class HeaderService {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly location = inject(Location);
  private readonly store = inject(Store<AppState>);
  private readonly routerService = inject(RouterService);
  private readonly supportService = inject(SupportService);
  private readonly navigationService = inject(StepperNavigationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly staffPipe = new TeacherNamePipe();

  // ── Signals exposed to the header shell ──

  readonly showHeader = signal(true);
  readonly showBackButton = signal(true);
  readonly title = signal<string | null>(null);
  readonly actionsTemplate = signal<TemplateRef<unknown> | null>(null);

  private readonly backHandler = signal<(() => boolean) | null>(null);

  // ── Store-derived signals for title resolution ──

  private readonly selectedStudent = toSignal(this.store.select(selectSelectedStudent));
  private readonly selectedEvent = toSignal(this.store.select(selectSelectedEvent));
  private readonly selectedClass = toSignal(this.store.select(selectSelectedClass));
  private readonly selectedSchool = toSignal(this.store.select(selectSelectedSchool));
  private readonly selectedActivity = toSignal(this.store.select(selectSelectedActivity));
  private readonly selectedAccount = toSignal(this.store.select(selectSelectedAccountLinks));

  // ── Manual title override ──
  private manualTitleActive = signal(false);

  constructor() {
    this.initRouterEvents();
  }

  // ── Actions template management ──

  setActions(template: TemplateRef<unknown>): void {
    this.actionsTemplate.set(template);
  }

  clearActions(): void {
    this.actionsTemplate.set(null);
  }

  // ── Manual title API ──

  /**
   * Sets a manual title that will not be overwritten by route config
   * until the next navigation starts.
   */
  setManualTitle(title: string | null): void {
    this.title.set(title);
    this.manualTitleActive.set(true);
  }

  // ── Back navigation ──

  setBackHandler(handler: () => boolean): void {
    this.backHandler.set(handler);
  }

  clearBackHandler(): void {
    this.backHandler.set(null);
  }

  goBack(): void {
    const contextualBack = this.backHandler();
    if (contextualBack && contextualBack()) {
      return;
    }

    const handled = this.navigationService.handleBack();
    if (handled) {
      return;
    }

    const currentUrl = this.router.url;

    if (currentUrl.includes('/payments/statements')) {
      this.router.navigate(['/payments/choose-account']);
      return;
    }

    if (currentUrl.includes('/payments/choose-account')) {
      this.router.navigate(['/payments']);
      return;
    }

    // Handle guardian profile/access subroutes → go back to profile
    if (/\/guardians\/[^/]+\/profile\/access\/.+/.exec(currentUrl)) {
      const profileUrl = currentUrl.substring(0, currentUrl.indexOf('/profile') + '/profile'.length);
      this.router.navigate([profileUrl]);
      return;
    }

    // Handle guardian profile → go back to guardians list
    if (/\/guardians\/[^/]+\/profile$/.exec(currentUrl)) {
      this.router.navigate(['/guardians']);
      return;
    }

    const urlSegments = currentUrl.split('/');
    if (currentUrl.includes('/payments') || currentUrl.includes('/guardians')) {
      if (urlSegments.length > 2) {
        urlSegments.pop();
        const parentUrl = urlSegments.join('/');
        this.router.navigate([parentUrl]);
      } else {
        this.router.navigate(['/']);
      }
      return;
    }

    // If there's no tracked previous URL, or if it's an auth page
    // (which would redirect back here creating a loop), go home instead.
    const previousUrl = this.routerService.previousUrl();
    if (
      !previousUrl ||
      ((previousUrl.startsWith('/login') ||
        previousUrl.startsWith('/activate') ||
        previousUrl.startsWith('/unauthorised')) &&
        (previousUrl.includes('/password-reset') || previousUrl.includes('/username-recovery')))
    ) {
      this.router.navigate(['/']);
    } else {
      this.location.back();
    }
  }

  // ── Private: router event handling ──

  private initRouterEvents(): void {
    // Clear stale actions template on every navigation start
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.clearActions();
          this.clearBackHandler();
          this.manualTitleActive.set(false); // allow the new route to set its own title
        },
        error: (err) => console.error('Header actions clear failed', err),
      });

    // Update header state on navigation end
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        mergeMap((route) => route.data),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (data) => {
          // If a manual title is active, skip applying the route config title
          if (this.manualTitleActive()) {
            return;
          }
          const config = data['headerConfig'] as HeaderConfig | undefined;
          if (config) {
            this.applyHeaderConfig(config);
          } else {
            console.warn('Route missing headerConfig:', this.router.url);
          }
        },
        error: (err) => console.error('Header route data subscription failed', err),
      });
  }

  private applyHeaderConfig(config: HeaderConfig): void {
    this.showHeader.set(config.showHeader ?? true);
    this.showBackButton.set(config.showBackButton ?? true);
    this.resolveTitleFromSource(config.titleSource);
  }

  private resolveTitleFromSource(source: TitleSource): void {
    switch (source.type) {
      case 'static':
        this.title.set(source.value);
        break;
      case 'student':
        this.title.set(this.selectedStudent()?.preferredName + "'s " + source.suffix);
        break;
      case 'event':
        this.title.set(this.selectedEvent()?.name ?? '');
        break;
      case 'class':
        this.title.set(
          (this.selectedClass()?.subject?.name ?? '') + ' | ' + this.staffPipe.transform(this.selectedClass()?.teacher),
        );
        break;
      case 'school':
        this.title.set(this.selectedSchool()?.name + "'s " + source.suffix);
        break;
      case 'activity':
        this.title.set(this.selectedActivity()?.name + ' ' + source.suffix);
        break;
      case 'ticket':
        this.title.set(this.supportService.selectedTicket()?.referenceCode ?? 'Ticket');
        break;
      case 'accountCode':
        this.title.set(this.selectedAccount()?.accountCode + ' ' + source.suffix);
        break;
      case 'dynamic':
        this.title.set(this.resolveDynamicTitle());
        break;
      case 'logo':
        this.title.set(null);
        break;
    }
  }

  private resolveDynamicTitle(): string | null {
    let currentRoute = this.activatedRoute;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    const title = currentRoute.snapshot.paramMap.get('title');
    if (title) {
      return title.replaceAll('-', ' ').replaceAll(/\b\w/g, (char) => char.toUpperCase());
    }

    return null;
  }
  forceClearManualTitle(): void {
    this.manualTitleActive.set(false);
    this.title.set(null);
    this.showHeader.set(true);
    this.showBackButton.set(true);
    this.clearActions();
    this.clearBackHandler();
  }
}
